// Backend integration point: replace these scoring functions with API calls to scoring service

export type ScoreBand = 'elite' | 'high' | 'solid' | 'developing' | 'limited';

export interface ScoreBandInfo {
  band: ScoreBand;
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  min: number;
  max: number;
}

export const SCORE_BANDS: ScoreBandInfo[] = [
  { band: 'elite', label: 'Elite', color: '#FF3B30', bgColor: 'rgba(255,59,48,0.12)', borderColor: 'rgba(255,59,48,0.3)', min: 90, max: 100 },
  { band: 'high', label: 'High Level', color: '#FF6B35', bgColor: 'rgba(255,107,53,0.10)', borderColor: 'rgba(255,107,53,0.25)', min: 80, max: 89 },
  { band: 'solid', label: 'Solid', color: '#10B981', bgColor: 'rgba(16,185,129,0.10)', borderColor: 'rgba(16,185,129,0.25)', min: 70, max: 79 },
  { band: 'developing', label: 'Developing', color: '#F59E0B', bgColor: 'rgba(245,158,11,0.10)', borderColor: 'rgba(245,158,11,0.25)', min: 60, max: 69 },
  { band: 'limited', label: 'Major Limitation', color: '#B3B3B3', bgColor: 'rgba(179,179,179,0.08)', borderColor: 'rgba(179,179,179,0.2)', min: 0, max: 59 },
];

export function getScoreBand(score: number): ScoreBandInfo {
  return SCORE_BANDS.find(b => score >= b.min && score <= b.max) ?? SCORE_BANDS[4];
}

export function getEpsColor(score: number): string {
  if (score >= 90) return '#FF3B30';
  if (score >= 80) return '#FF6B35';
  if (score >= 70) return '#10B981';
  if (score >= 60) return '#F59E0B';
  return '#B3B3B3';
}

// Engine scoring standards (0–100 normalization)
// Based on relative strength ratios (lift / bodyweight)
export function scoreEngine(inputs: EngineInputs): number {
  const { trapBarDL, backSquat, frontSquat, splitSquat, bodyweight } = inputs;
  if (!bodyweight || bodyweight <= 0) return 0;

  const tbRatio = trapBarDL / bodyweight;
  const bsRatio = backSquat / bodyweight;
  const fsRatio = frontSquat / bodyweight;
  const ssRatio = splitSquat / bodyweight;

  // Scoring rubric per ratio (elite ~2.5x BW trap bar, 2.0x back squat, 1.7x front squat, 1.5x split squat)
  const tbScore = Math.min(100, (tbRatio / 2.5) * 100);
  const bsScore = Math.min(100, (bsRatio / 2.0) * 100);
  const fsScore = Math.min(100, (fsRatio / 1.7) * 100);
  const ssScore = Math.min(100, (ssRatio / 1.5) * 100);

  const weights = [0.35, 0.30, 0.20, 0.15];
  const raw = tbScore * weights[0] + bsScore * weights[1] + fsScore * weights[2] + ssScore * weights[3];
  return Math.round(Math.min(100, Math.max(0, raw)));
}

// Ignition scoring standards
export function scoreIgnition(inputs: IgnitionInputs): number {
  const { cmjHeight, sqjHeight, sprint10yd, sprint20yd, medBallThrow } = inputs;

  // CMJ: elite ~32in, developing ~22in
  const cmjScore = Math.min(100, (cmjHeight / 32) * 100);
  // SQJ: elite ~28in
  const sqjScore = Math.min(100, (sqjHeight / 28) * 100);
  // 10yd: elite ~1.45s, developing ~1.75s (lower is better)
  const sprint10Score = Math.min(100, Math.max(0, ((1.75 - sprint10yd) / (1.75 - 1.45)) * 100));
  // 20yd: elite ~2.55s, developing ~3.10s
  const sprint20Score = Math.min(100, Math.max(0, ((3.10 - sprint20yd) / (3.10 - 2.55)) * 100));
  // Med ball: elite ~45ft
  const mbScore = Math.min(100, (medBallThrow / 45) * 100);

  const weights = [0.30, 0.20, 0.25, 0.15, 0.10];
  const raw = cmjScore * weights[0] + sqjScore * weights[1] + sprint10Score * weights[2] + sprint20Score * weights[3] + mbScore * weights[4];
  return Math.round(Math.min(100, Math.max(0, raw)));
}

// Bounce scoring standards
export function scoreBounce(inputs: BounceInputs): number {
  const { rsi, dropJumpHeight, broadJump, tripleJump, pogoHeight } = inputs;

  // RSI: elite ~2.5, developing ~1.0
  const rsiScore = Math.min(100, (rsi / 2.5) * 100);
  // Drop jump: elite ~26in
  const djScore = Math.min(100, (dropJumpHeight / 26) * 100);
  // Broad jump: elite ~110in (9'2"), developing ~80in
  const bjScore = Math.min(100, (broadJump / 110) * 100);
  // Triple jump: elite ~330in (27'6")
  const tjScore = Math.min(100, (tripleJump / 330) * 100);
  // Pogo: elite ~12in
  const pogoScore = Math.min(100, (pogoHeight / 12) * 100);

  const weights = [0.30, 0.25, 0.20, 0.15, 0.10];
  const raw = rsiScore * weights[0] + djScore * weights[1] + bjScore * weights[2] + tjScore * weights[3] + pogoScore * weights[4];
  return Math.round(Math.min(100, Math.max(0, raw)));
}

export function calculateEPS(
  engineScore: number,
  ignitionScore: number,
  bounceScore: number,
  weights = { engine: 0.35, ignition: 0.35, bounce: 0.30 }
): number {
  return Math.round(
    engineScore * weights.engine +
    ignitionScore * weights.ignition +
    bounceScore * weights.bounce
  );
}

export interface EngineInputs {
  trapBarDL: number;
  backSquat: number;
  frontSquat: number;
  splitSquat: number;
  bodyweight: number;
}

export interface IgnitionInputs {
  cmjHeight: number;
  sqjHeight: number;
  sprint10yd: number;
  sprint20yd: number;
  medBallThrow: number;
}

export interface BounceInputs {
  rsi: number;
  dropJumpHeight: number;
  broadJump: number;
  tripleJump: number;
  pogoHeight: number;
}

export interface ExplosiveProfile {
  engineScore: number;
  ignitionScore: number;
  bounceScore: number;
  eps: number;
  strengths: string[];
  limiters: string[];
  primaryPriority: string;
  interpretation: string;
  programFocus: 'engine' | 'ignition' | 'bounce' | 'balanced';
}

// Rule-based interpretation engine
export function generateInterpretation(
  engineScore: number,
  ignitionScore: number,
  bounceScore: number
): ExplosiveProfile {
  const eps = calculateEPS(engineScore, ignitionScore, bounceScore);

  const scores = [
    { name: 'Engine', score: engineScore },
    { name: 'Ignition', score: ignitionScore },
    { name: 'Bounce', score: bounceScore },
  ].sort((a, b) => b.score - a.score);

  const strengths = scores.slice(0, 2).map(s => `${s.name} (${s.score})`);
  const limiters = scores.slice(-2).reverse().map(s => `${s.name} (${s.score})`);

  const lowestCategory = scores[scores.length - 1];
  const highestCategory = scores[0];

  let interpretation = '';
  let primaryPriority = '';
  let programFocus: ExplosiveProfile['programFocus'] = 'balanced';

  const gap = highestCategory.score - lowestCategory.score;

  if (engineScore >= 75 && ignitionScore < 65) {
    interpretation = 'Strong athlete who cannot express force quickly. High force ceiling exists but rate of force development is the limiting factor in explosive output.';
    primaryPriority = 'Rate of Force Development — Explosive Lift Emphasis';
    programFocus = 'ignition';
  } else if (ignitionScore >= 75 && bounceScore < 65) {
    interpretation = 'Explosive athlete who lacks elastic efficiency and reactivity. Good initial force expression but poor energy return from tendons and reactive structures.';
    primaryPriority = 'Elastic & Reactive Power — Plyometric Emphasis';
    programFocus = 'bounce';
  } else if (bounceScore >= 75 && engineScore < 65) {
    interpretation = 'Reactive athlete with a low force ceiling. Excellent energy recycling and elastic power, but insufficient raw strength to amplify explosive output.';
    primaryPriority = 'Max Strength Development — Force Production Emphasis';
    programFocus = 'engine';
  } else if (engineScore < 65 && ignitionScore < 65 && bounceScore < 65) {
    interpretation = 'Explosive power system is underdeveloped across all three categories. Foundation building required before specialization.';
    primaryPriority = 'General Strength & Power Foundation';
    programFocus = 'engine';
  } else if (gap < 15 && eps >= 75) {
    interpretation = 'Well-developed explosive power system with balanced category scores. Athlete demonstrates consistent force production, rate of development, and elastic efficiency.';
    primaryPriority = 'Balanced Maintenance + Peak Performance';
    programFocus = 'balanced';
  } else {
    // Default: target the weakest
    const focusMap: Record<string, ExplosiveProfile['programFocus']> = {
      Engine: 'engine', Ignition: 'ignition', Bounce: 'bounce'
    };
    programFocus = focusMap[lowestCategory.name] ?? 'engine';
    interpretation = `Explosive profile shows a clear gap in ${lowestCategory.name} capacity. ${lowestCategory.name === 'Engine' ? 'Strength and force production are the primary limiters.' : lowestCategory.name === 'Ignition' ? 'Rate of force development is limiting explosive expression.' : 'Elastic and reactive power are limiting energy efficiency.'}`;
    primaryPriority = `${lowestCategory.name} Development — ${lowestCategory.name === 'Engine' ? 'Strength Emphasis' : lowestCategory.name === 'Ignition' ? 'Explosive Lift Emphasis' : 'Plyometric Emphasis'}`;
  }

  return { engineScore, ignitionScore, bounceScore, eps, strengths, limiters, primaryPriority, interpretation, programFocus };
}